import { useTrackLink } from '@seek/cmsu-analytics';
import { CFormType, type FormFragment } from '@seek/cmsu-cms-connect';
import { RichText } from '@seek/cmsu-rich-text';
import type { RenderParams } from '@seek/forms-ui';
import type { UseField } from '@seek/forms-ui/lib/Field/useField';
import type { Language } from '@seek/forms-ui/lib/types';
import type { Country, SiteName } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Button,
  FieldMessage,
  Heading,
  IconPositive,
  Inline,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import { useState } from 'react';

import { ActionGroup } from '..';
import { ReCAPTCHA } from '../../shared/recaptcha';

import translations from './.vocab';
import { FormFieldsRenderer } from './FormFieldsRenderer';
import { FormHeader } from './FormHeader';
import { prepareFormData } from './helper';
import type { DynamicFormValues, FormField } from './types';

type FormProps = RenderParams<DynamicFormValues>;
type BraidButtonProps = React.ComponentProps<typeof Button>;

export interface ComponentProps extends FormProps {
  useField: UseField<DynamicFormValues, Language>;
  country: Country;
  locale: string;
  site: SiteName;
  wptSalesForceApi: string;
  resetForm: () => void;
  sourceName: string;
  privacyUrl: string;
  form: FormFragment;
  recaptchaKey: string;
  sectionName?: string;
}

export const Form = ({
  handleSubmit,
  useField,
  resetForm,
  wptSalesForceApi,
  country,
  site,
  locale,
  sourceName,
  form,
  recaptchaKey,
  sectionName,
}: ComponentProps) => {
  const { t } = useTranslations(translations);
  const isMailChimpForm = form.formType === CFormType.MailChimp;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<string>('');
  const [recaptchaValue, setRecaptchaValue] = useState('');

  // Tealium Tracking when the form is submitted and receive API response
  const onSubmitLink = useTrackLink('form-submission', {
    elementAction: form.ctaText ?? 'Submit',
    elementText: form.ctaText ?? 'Submit',
    elementDesign: 'button',
  });

  // Tealium Tracking when the button is pressed
  const onSubmitPressed = useTrackLink('button-link', {
    elementAction: form.ctaText ?? 'Submit',
    elementText: form.ctaText ?? 'Submit',
    elementDesign: 'button',
  });

  const onSubmit = handleSubmit((formValues: DynamicFormValues) => {
    onSubmitPressed?.();
    if (!recaptchaValue) {
      setRecaptchaError('Please verify that you are not a robot');
      return;
    }

    // Get the data and url by checking the form type
    const { data, url } = prepareFormData(
      form,
      formValues,
      wptSalesForceApi,
      country,
      site,
      sourceName,
      recaptchaValue,
    );

    fetch(url, {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (response.ok) {
          setIsSubmitted(true);
          onSubmitLink?.();
        } else {
          resetForm();
          setSubscriptionError(true);
        }
      })
      .catch(() => {
        setSubscriptionError(true);
        resetForm();
      });
  });

  return (
    <form onSubmit={onSubmit} style={{ padding: '0px' }}>
      {!isSubmitted ? (
        <Box>
          <Stack space="medium">
            <FormHeader
              title={form.title}
              description={form.description}
              sectionName={sectionName}
              formType={form.formType}
            />
            <Tiles
              columns={{
                mobile: 1,
                tablet: form.columns,
                desktop: form.columns,
                wide: form.columns,
              }}
              space="medium"
            >
              <FormFieldsRenderer
                fields={form.fields ?? []}
                useField={useField}
              />
            </Tiles>

            <ReCAPTCHA
              style={{ paddingTop: '0px', marginTop: '0px' }}
              sitekey={recaptchaKey}
              onChange={(val: any) => {
                setRecaptchaValue(val);
                setRecaptchaError('');
              }}
              hl={locale}
            />

            {Boolean(recaptchaError) && (
              <RecaptchaErrorMessage recaptchaError={recaptchaError} />
            )}
            {form.consentText && <ConsentText form={form} />}
            <FormButton form={form} isMailChimpForm={isMailChimpForm} />

            {subscriptionError && (
              <Box>
                <Alert tone="critical">
                  <Text>{t('Something Wrong')}</Text>
                </Alert>
              </Box>
            )}
          </Stack>
        </Box>
      ) : (
        <>
          {form.confirmationMessage && isMailChimpForm ? (
            <MailChimpFormConfirmationMessage form={form} />
          ) : (
            <FormConfirmationMessage form={form} />
          )}
        </>
      )}
    </form>
  );
};

const ConsentText = ({ form }: { form: FormFragment }) => (
  <RichText
    content={form?.consentText?.raw}
    renderers={{
      p: ({ children }) => <Text size="xsmall">{children}</Text>,
    }}
  />
);

const FormButton = ({
  form,
  isMailChimpForm,
}: {
  form: FormFragment;
  isMailChimpForm: boolean;
}) => {
  const { t } = useTranslations(translations);
  return (
    <Inline space="none">
      <Button
        type="submit"
        id="submitBtn"
        tone={
          isMailChimpForm
            ? 'formAccent'
            : (form.buttonColour as BraidButtonProps['tone']) || undefined
        }
      >
        {form.ctaText || t('Submit')}
      </Button>
    </Inline>
  );
};

const RecaptchaErrorMessage = ({
  recaptchaError,
}: {
  recaptchaError: string;
}) => <FieldMessage id="recaptcha" tone="critical" message={recaptchaError} />;

const MailChimpFormConfirmationMessage = ({ form }: { form: FormFragment }) => {
  const { t } = useTranslations(translations);
  return (
    <Box>
      <Stack space="medium">
        <Heading level="2">Subscribe</Heading>
        <Text icon={<IconPositive />}>{t('Thanks for subscribing')}</Text>
      </Stack>
      {form.confirmationAction?.actions && (
        <ActionGroup
          size={form.confirmationAction?.actionGroupSize}
          {...form.confirmationAction}
        />
      )}
    </Box>
  );
};

const FormConfirmationMessage = ({ form }: { form: FormFragment }) => (
  <Box data={{ testid: 'FormConfirmationMessageTestId' }}>
    <Stack space="medium">
      <RichText content={form.confirmationMessage?.raw} />
      {form.confirmationAction?.actions && (
        <ActionGroup
          size={form.confirmationAction?.actionGroupSize}
          {...form.confirmationAction}
        />
      )}
    </Stack>
  </Box>
);

export const gridColumn = (field: FormField) =>
  field.__typename === 'FormTextarea' ||
  field.__typename === 'FormCheckbox' ||
  field.__typename === 'FormHiddenFieldGroup' ||
  field.__typename === 'FormFieldGroup' ||
  field.rowSpan
    ? '1 / -1'
    : 'auto';
